import React, { useState, useEffect } from 'react';
import { getCategories, getLastPostByCategory, getThreadCountByCategory, getPostCountByCategory, getUserDataById } from '../../utils/api';

import { Routes, Route, Link } from 'react-router-dom';
import CategoryThreads from '../threads/CategoryThreads';
import Thread from '../threads/Thread';
import Post from '../posts/Post';
import Tests from '../shorts/Tests';
import News from '../shorts/News';
import { Helmet } from 'react-helmet-async';
import AdSlot from '../ads/AdSlot';

import '../../assets/css/forum.css';
import CreateThread from '../threads/CreateThread';
import CreatePost from '../posts/CreatePost';

const Community = () => {
    const [categories, setCategories] = useState([]);
    const [lastPosts, setLastPosts] = useState({}); // Speichert die letzten Beiträge für Kategorien
    const [threadCounts, setThreadCounts] = useState({});
    const [postCounts, setPostCounts] = useState({});
    const [userData, setUserData] = useState({}); // Speichert Benutzerdaten
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await getCategories();
                setCategories(data.categories || []);

                const lastPostPromises = data.categories.map(async (category) => {
                    const lastPost = await getLastPostByCategory(category.id);
                    return { categoryId: category.id, lastPost: lastPost?.lastPost || null };
                });

                const threadCountPromises = data.categories.map(async (category) => {
                    const threadCount = await getThreadCountByCategory(category.id);
                    return { categoryId: category.id, count: threadCount?.threadCount || 0 };
                });

                const postCountPromises = data.categories.map(async (category) => {
                    const postCount = await getPostCountByCategory(category.id);
                    return { categoryId: category.id, count: postCount?.postCount || 0 };
                });

                const [lastPostsArray, threadCountsArray, postCountsArray] = await Promise.all([
                    Promise.all(lastPostPromises),
                    Promise.all(threadCountPromises),
                    Promise.all(postCountPromises),
                ]);

                setLastPosts(lastPostsArray.reduce((acc, item) => ({ ...acc, [item.categoryId]: item.lastPost }), {}));
                setThreadCounts(threadCountsArray.reduce((acc, item) => ({ ...acc, [item.categoryId]: item.count }), {}));
                setPostCounts(postCountsArray.reduce((acc, item) => ({ ...acc, [item.categoryId]: item.count }), {}));

                // Hole die Benutzerdaten für den letzten Beitrag
                const userPromises = lastPostsArray.map(async (post) => {
                    if (post.lastPost) {
                        const user = await getUserDataById(post.lastPost.user_id);
                        return { userId: post.lastPost.user_id, userData: user };
                    }
                    return null;
                });

                const usersData = await Promise.all(userPromises);
                const usersMap = usersData.reduce((acc, item) => {
                    if (item) acc[item.userId] = item.userData;
                    return acc;
                }, {});

                setUserData(usersMap);
            } catch (err) {
                setError("Fehler beim Laden der Kategorien");
            } finally {
                setLoading(false);
            }
        };

        loadCategories();
    }, []);

    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} um ${hours}:${minutes} Uhr`;
    };

    if (loading) {
        return <div>Loading categories...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    const pageTitle = 'Community | NGCTV';
    const pageDescription = 'Diskutiere mit der NGCTV-Community: Kategorien, Threads und Beiträge im Überblick.';

    return (
        <>
            {/* SEO / Head */}
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <link rel="canonical" href={`https://ngctv.de/community`} />
            </Helmet>

            <Routes>
                <Route
                    path="/"
                    element={
                        <>
                            <main>
                                <div className='wrapper'>
                                    <div className='rows'>
                                        <div className='row-forum'>
                                            <div className='category'>Community</div>
                                            <div className='breadcrumb'>
                                                <ul>
                                                    <li>Community</li>
                                                </ul>
                                            </div>
                                            <table className='forum'>
                                                <thead>
                                                    <tr>
                                                        <th>Board</th>
                                                        <th>Inhalt</th>
                                                        <th>Letzter Beitrag</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {categories.map((category) => (
                                                        <tr key={category.id}>
                                                            <td>
                                                                <div className="category-title">
                                                                    <a as={Link} href={`/community/${category.name}`} viewtransition="true">{category.name}</a>
                                                                </div>
                                                                <div className="category-description">
                                                                    {category.description}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="category-themes">{threadCounts[category.id] || 0} Themen</div>
                                                                <div className="category-entrys">{postCounts[category.id] || 0} Beiträge</div>
                                                            </td>
                                                            <td>
                                                                {lastPosts[category.id] ? (
                                                                    <>
                                                                        <div className="category-description">
                                                                            {formatDate(lastPosts[category.id].created_at)}
                                                                        </div>
                                                                        <div className="category-description">
                                                                            von <strong>
                                                                                {userData[lastPosts[category.id].user_id] ? (
                                                                                    `${userData[lastPosts[category.id].user_id].username}`
                                                                                ) : (
                                                                                    "Unbekannt"
                                                                                )}
                                                                            </strong>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div>—</div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className='row'>
                                            <Tests />
                                            <News />
                                            <AdSlot slot="2119524913" />
                                        </div>
                                    </div>
                                </div>
                            </main>
                        </>
                    }
                />
                <Route path="/:categoryName" element={<CategoryThreads />} />
                <Route path="/:categoryName/:id" element={<Thread />} />
                <Route path="/thread/:threadId/post/:postId" element={<Post />} />

                <Route path="/create-thread" element={<CreateThread />} />
                <Route path="/create-post" element={<CreatePost />} />
            </Routes>
        </>
    );
};

export default Community;
